import type {
  CompleteUploadInput,
  DownloadUrlOutput,
  File as FileResource,
  ListFilesResult,
  PreviewThumbnailOutput,
  RequestUploadUrlInput,
  RequestUploadUrlOutput,
} from '@terafile/shared'
import { apiFetch } from './api.js'

/**
 * ファイル関連の API クライアント関数群。
 *
 * - アップロードは API → 署名URL発行 → クライアントが直接 R2 にアップロード（中継しない）
 * - DL も同様に API は署名URL返却のみ
 */

export type ListFilesParams = {
  archived?: boolean
  page?: number
  limit?: number
}

export async function listFiles(
  projectId: string,
  params: ListFilesParams = {},
): Promise<ListFilesResult> {
  const usp = new URLSearchParams()
  if (params.archived !== undefined) usp.set('archived', String(params.archived))
  if (params.page !== undefined) usp.set('page', String(params.page))
  if (params.limit !== undefined) usp.set('limit', String(params.limit))
  const qs = usp.toString()
  return apiFetch<ListFilesResult>(`/projects/${projectId}/files${qs ? `?${qs}` : ''}`)
}

export async function requestUploadUrl(
  projectId: string,
  input: RequestUploadUrlInput,
): Promise<RequestUploadUrlOutput> {
  return apiFetch<RequestUploadUrlOutput>(`/projects/${projectId}/files/upload-url`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function completeUpload(
  projectId: string,
  fileId: string,
  input: CompleteUploadInput,
): Promise<FileResource> {
  return apiFetch<FileResource>(`/projects/${projectId}/files/${fileId}/complete`, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

export async function getDownloadUrl(fileId: string): Promise<DownloadUrlOutput> {
  return apiFetch<DownloadUrlOutput>(`/files/${fileId}/download-url`)
}

/**
 * 動画ファイルの署名済みサムネイル URL を個別に取得する（lazy 戦略・パフォーマンス改善）。
 * 一覧 API ではサムネ URL を返さず、画面に入った時点でこの API を叩く。
 */
export async function getPreviewThumbnailUrl(fileId: string): Promise<PreviewThumbnailOutput> {
  return apiFetch<PreviewThumbnailOutput>(`/files/${fileId}/preview-thumbnail`)
}

/**
 * アップロード中のファイルを中断（×ボタン押下時の掃除）。
 * - DB の暫定 File レコードを削除
 * - multipart の場合 R2 側の in-flight upload を AbortMultipartUpload で中断
 *
 * 既にアップロード完了済みなら 409 で弾かれる（完了済みは通常の deleteFile を使う）。
 */
export async function abortFileUpload(
  projectId: string,
  fileId: string,
  uploadId?: string,
): Promise<void> {
  await apiFetch<void>(`/projects/${projectId}/files/${fileId}/abort`, {
    method: 'DELETE',
    body: JSON.stringify(uploadId ? { uploadId } : {}),
  })
}

/**
 * ファイル単位の完全削除（R2 + Stream + DB を物理削除、復元不可）。
 * 204 / 202（外部削除リトライ中）の両方を成功として扱う。
 */
export async function deleteFile(fileId: string): Promise<void> {
  await apiFetch<void>(`/files/${fileId}`, { method: 'DELETE' })
}

/**
 * ファイルをゴミ箱へ移す（内部: archived_at をマーク）。30日後に自動完全削除される。
 */
export async function archiveFile(fileId: string): Promise<FileResource> {
  return apiFetch<FileResource>(`/files/${fileId}/archive`, { method: 'POST' })
}

/**
 * ファイルをゴミ箱から元に戻す。
 */
export async function unarchiveFile(fileId: string): Promise<FileResource> {
  return apiFetch<FileResource>(`/files/${fileId}/unarchive`, { method: 'POST' })
}

/**
 * 表示用ファイル名を変更する。
 * storageKey / Stream uid は変更されない（表示名のみ）。
 */
export async function renameFile(fileId: string, originalName: string): Promise<FileResource> {
  return apiFetch<FileResource>(`/files/${fileId}/rename`, {
    method: 'POST',
    body: JSON.stringify({ originalName }),
  })
}
